import { useState } from "react";
import { fetchJson } from "../utils/http";
import { SERVER_URL } from "../../../constants";
import { pick } from "../utils/objects";
import { listNsrsForAdult, updateNsr } from "../api/people";
import { settleWithConcurrency } from "../utils/concurrency";
import { jsonOr } from "../../../utils/http";

// Cap concurrent writes so a bulk "Save all" doesn't overrun the backend's
// JDBC connection pool. Each adult can fan out several PUTs (adult + NSR +
// res-charge), so keep this modest.
const SAVE_CONCURRENCY = 4;

export function toAdultDraft(raw) {
  return {
    id: pick(raw, "id"),
    dateAdded: pick(raw, "dateAdded", "date_added"),
    adultName: pick(raw, "adultName", "adult_name"),
    adultSurname: pick(raw, "adultSurname", "adult_surname"),
    adultEmail: pick(raw, "adultEmail", "adult_email"),
    adultPhone: pick(raw, "adultPhone", "adult_phone"),
    adultNationality: pick(raw, "adultNationality", "adult_nationality"),
    allergies: pick(raw, "allergies"),
    notes: pick(raw, "notes"),
  };
}

export function toAdultPayload(d) {
  return {
    dateAdded: d.dateAdded,
    adultName: d.adultName ?? "",
    adultSurname: d.adultSurname ?? "",
    adultEmail: d.adultEmail ?? "",
    adultPhone: d.adultPhone ?? "",
    adultNationality: d.adultNationality ?? "",
    allergies: d.allergies ?? "",
    notes: d.notes ?? "",
  };
}

// Build full NSR payload from stored raw + edits so required fields are always present
function buildNsrPayload(nsr) {
  const raw = nsr._raw || {};
  return {
    arrivaldate: raw.arrivaldate || raw.arrivalDate,
    departuredate: raw.departuredate || raw.departureDate,
    residentialBase: raw.residentialBase || raw.residential_base,
    notes: raw.notes ?? "",
    overnightResident: nsr.overnightResident ?? false,
    gender: nsr.overnightResident ? (nsr.gender || null) : null,
  };
}

export function useEditableAdults({ token, onListPatched }) {
  const [editRows, setEditRows] = useState({});
  const [dirtyIds, setDirtyIds] = useState(new Set());

  // NSR data per adult: { [adultId]: { nsrId, overnightResident, gender, loading } }
  const [nsrData, setNsrData] = useState({});

  // Adult bookings per adult: { [adultId]: { bookings, resCharge, loading } }
  const [bookingData, setBookingData] = useState({});
  // Edited charge totals: { [adultId]: number }
  const [editedCharges, setEditedCharges] = useState({});

  const fetchNsrForAdult = async (adultId) => {
    setNsrData((prev) => ({ ...prev, [adultId]: { ...(prev[adultId] || {}), loading: true } }));
    try {
      const nsrs = await listNsrsForAdult(token, adultId);
      // Take the first NSR (most relevant for booking group context)
      const nsr = nsrs.length > 0 ? nsrs[0] : null;
      setNsrData((prev) => ({
        ...prev,
        [adultId]: {
          nsrId: nsr?.id || null,
          overnightResident: nsr?.overnightResident ?? nsr?.overnight_resident ?? false,
          gender: nsr?.gender || null,
          // Keep full record so we can send required fields back on save
          _raw: nsr,
          loading: false,
        },
      }));
    } catch (e) {
      console.error(`Failed to fetch NSR for adult ${adultId}`, e);
      setNsrData((prev) => ({
        ...prev,
        [adultId]: { nsrId: null, overnightResident: false, gender: null, loading: false },
      }));
    }
  };

  const fetchBookingsForAdult = async (adultId) => {
    setBookingData((prev) => ({ ...prev, [adultId]: { ...(prev[adultId] || {}), loading: true } }));
    try {
      const res = await fetch(`${SERVER_URL}api/adults/${adultId}/adult-bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await jsonOr(res, []);
      const bookings = Array.isArray(data) ? data : [];
      const isExtraNight = (b) =>
        (b.productName || "").toLowerCase().includes("night");
      const resBookings = bookings.filter((b) => b.participationType === "BOOKING" && !isExtraNight(b));
      const resCharge = resBookings.reduce((sum, b) => sum + (Number(b.actualCharge) || 0), 0);
      setBookingData((prev) => ({
        ...prev,
        [adultId]: { bookings: resBookings, resCharge, loading: false },
      }));
    } catch (e) {
      console.error(`Failed to fetch bookings for adult ${adultId}`, e);
      setBookingData((prev) => ({
        ...prev,
        [adultId]: { bookings: [], resCharge: 0, loading: false },
      }));
    }
  };

  const startEdit = (raw) => {
    const d = toAdultDraft(raw);
    setEditRows((prev) => ({ ...prev, [d.id]: d }));
    // Fetch NSR if not already loaded
    if (!nsrData[d.id] || nsrData[d.id].nsrId === undefined) {
      fetchNsrForAdult(d.id);
    }
    // Fetch bookings if not already loaded
    if (!bookingData[d.id]) {
      fetchBookingsForAdult(d.id);
    }
  };

  const cancelEdit = (id) => {
    setEditRows((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
    setDirtyIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const changeField = (id, field, value) => {
    setEditRows((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
    setDirtyIds((prev) => new Set(prev).add(id));
  };

  const changeResCharge = (adultId, value) => {
    setEditedCharges((prev) => ({ ...prev, [adultId]: value }));
    setDirtyIds((prev) => new Set(prev).add(adultId));
  };

  const changeNsrField = (adultId, field, value) => {
    setNsrData((prev) => ({
      ...prev,
      [adultId]: { ...(prev[adultId] || {}), [field]: value },
    }));
    setDirtyIds((prev) => new Set(prev).add(adultId));
  };

  // Spread a total charge evenly across residential bookings, remainder on the last
  const saveResCharge = async (adultId) => {
    const newTotal = Number(editedCharges[adultId]);
    if (isNaN(newTotal) || newTotal < 0) return;
    const bd = bookingData[adultId];
    if (!bd?.bookings?.length) return;

    const count = bd.bookings.length;
    const perBooking = Math.floor(newTotal * 100 / count) / 100; // round down to 2dp
    const remainder = Math.round((newTotal - perBooking * count) * 100) / 100;

    for (let i = 0; i < count; i++) {
      const b = bd.bookings[i];
      const charge = i === count - 1 ? perBooking + remainder : perBooking;
      await fetchJson(`${SERVER_URL}api/adult-bookings/${b.id}`, token, {
        method: "PUT",
        body: JSON.stringify({
          participationType: b.participationType || "BOOKING",
          countsForTransport: b.countsForTransport ?? true,
          countsForRatio: b.countsForRatio ?? false,
          notes: b.notes ?? "",
          customStart: b.customStart || null,
          customEnd: b.customEnd || null,
          actualCharge: charge,
        }),
      });
    }

    // Refresh booking data
    await fetchBookingsForAdult(adultId);
    setEditedCharges((prev) => {
      const next = { ...prev };
      delete next[adultId];
      return next;
    });
  };

  const saveRow = async (id) => {
    const d = editRows[id];
    if (!d) return;

    // Save adult fields
    await fetchJson(`${SERVER_URL}api/adults/${id}`, token, {
      method: "PUT",
      body: JSON.stringify(toAdultPayload(d)),
    });

    // Save NSR fields if we have an NSR record
    const nsr = nsrData[id];
    if (nsr?.nsrId) {
      await updateNsr(token, nsr.nsrId, buildNsrPayload(nsr));
    }

    // Save residential charge if edited
    if (editedCharges[id] != null) {
      await saveResCharge(id);
    }

    cancelEdit(id);
    onListPatched?.((prev) => prev.map((a) => (a.id === id ? { ...a, ...d } : a)));
  };

  // Bulk save. Runs each adult's writes through a bounded pool and reports
  // per-row outcomes instead of crashing on the first rejection. Succeeded rows
  // are cleared and the list refreshed; failed rows are kept dirty + in edit
  // mode for retry. Never throws — returns { ok, saved, failed }.
  const saveAll = async () => {
    const ids = Array.from(dirtyIds);
    if (!ids.length) return { ok: true, saved: [], failed: [] };

    const saveOne = async (id) => {
      if (editRows[id]) {
        await fetchJson(`${SERVER_URL}api/adults/${id}`, token, {
          method: "PUT",
          body: JSON.stringify(toAdultPayload(editRows[id])),
        });
      }

      const nsr = nsrData[id];
      if (nsr?.nsrId) {
        await updateNsr(token, nsr.nsrId, buildNsrPayload(nsr));
      }

      if (editedCharges[id] != null) {
        await saveResCharge(id);
      }
    };

    const results = await settleWithConcurrency(ids, saveOne, SAVE_CONCURRENCY);

    const saved = [];
    const failed = [];
    for (const r of results) {
      if (r.status === "fulfilled") saved.push(r.item);
      else failed.push({ id: r.item, error: r.reason });
    }

    if (saved.length) {
      const savedSet = new Set(saved);
      // Clear only the rows that actually saved; keep failed drafts editable.
      setEditRows((prev) => {
        const next = { ...prev };
        saved.forEach((id) => delete next[id]);
        return next;
      });
      setDirtyIds((prev) => {
        const next = new Set(prev);
        saved.forEach((id) => next.delete(id));
        return next;
      });
      onListPatched?.((prev) =>
        prev.map((a) => (savedSet.has(a.id) && editRows[a.id] ? { ...a, ...editRows[a.id] } : a))
      );
    }

    if (failed.length) {
      console.error("[useEditableAdults.saveAll] failures:", failed);
    }

    return { ok: failed.length === 0, saved, failed };
  };

  return {
    editRows, dirtyIds, startEdit, cancelEdit, changeField, saveRow, saveAll,
    nsrData, changeNsrField, fetchNsrForAdult,
    bookingData, editedCharges, changeResCharge, fetchBookingsForAdult,
  };
}
